/**
 * Plain figures: a count, grouped, a count drawn on a badge, and a percentage.
 *
 * Money is not here. `formatCurrency` takes PENNIES and spells its own symbol;
 * a figure that names an amount goes through that one, or the two surfaces
 * showing the same amount disagree by a factor of a hundred.
 */
import { numberFormatter } from "./intl.js";
/**
 * A count with thousands separators: `formatNumber(1234567)` is "1,234,567".
 *
 * Fractions survive to three places, which is `Intl`'s own default and the
 * behavior every consuming surface already renders; a figure needing a fixed
 * precision is a currency or a rate and has its own formatter.
 */
export function formatNumber(value, opts = {}) {
    const { locale = "en-US" } = opts;
    if (value === null || value === undefined || value === "")
        return "0";
    // `Number` reads the whole string or nothing. `Number.parseFloat` reads a
    // PREFIX, so it takes the already-formatted "1,999" for 1 and "12px" for 12 —
    // a value carrying a separator or a unit is display output rather than the
    // count this takes, and which number it names depends on a locale this has
    // not been told ("1.999" is a thousand in de-DE).
    const figure = typeof value === "string" ? Number(value) : value;
    if (!Number.isFinite(figure))
        return "0";
    return numberFormatter(locale, { style: "decimal" }).format(figure);
}
/**
 * A count drawn on a badge: exact up to 99, `"99+"` above, because a badge has
 * room for three characters.
 */
export function formatBadgeCount(count) {
    return count > 99 ? "99+" : String(count);
}
/**
 * A percentage given out of 100, rounded to the places it is printed at: the
 * number a reader sees, `printedPercent(89.6, 0)` is 90. `formatPercent` prints
 * exactly this figure, so a rule that judges a percentage against a threshold
 * judges this one and can never contradict the figure beside it: judged on the
 * raw 89.6, an "under 90%" would sit beside "90%".
 */
export function printedPercent(percent, fractionDigits) {
    return Number(percent.toFixed(fractionDigits));
}
/**
 * A percentage given out of 100, as an API serves a rate it stores to four
 * places of a fraction: `formatPercent(15)` is "15%", `formatPercent(12.25)` is
 * "12.25%". A whole percent reads whole, and `fractionDigits` sets the places
 * the figure is rounded to: `formatPercent(89.6, { fractionDigits: 0 })` is
 * "90%". A figure that is not a finite number reads "0%".
 *
 * The figure is `printedPercent`'s, rounded before `Intl` sees it, so the
 * percent printed is the one a threshold judges. Left to round alone, `Intl`
 * reads `percent / 100` by its shortest decimal, which can sit the other side
 * of a half: `0.565 * 100` is 56.49999999999999, which `printedPercent` rounds
 * to 56, but divided by 100 it is 0.565 again, which `Intl` prints "57%".
 */
export function formatPercent(percent, opts = {}) {
    const { locale = "en-US", fractionDigits = 2 } = opts;
    const figure = Number.isFinite(percent) ? printedPercent(percent, fractionDigits) : 0;
    return numberFormatter(locale, { style: "percent", maximumFractionDigits: fractionDigits }).format(figure / 100);
}
